// Wing — Capacitor native keyboard bootstrap.
//
// Runs once at startup on iOS/Android to:
//   · Hide the "^ v ✓" input-accessory bar (WhatsApp doesn't have it)
//   · Force resize mode = native so the WebView SHRINKS under the keyboard
//     instead of the keyboard covering our fixed composer
//   · On web/PWA (where there IS no native resize), expose a CSS variable
//     `--keyboard-h` on <html> so the chat wrapper can shrink to match
//
// CRITICAL: on native iOS/Android with `resize: native`, the WebView
// already shrinks the window itself.  If we ALSO publish `--keyboard-h`
// the chat wrapper would shrink a SECOND time → large black gap between
// the messages and the composer.  So we deliberately keep `--keyboard-h`
// at 0 on native platforms and rely purely on the WebView resize.
import { Capacitor } from "@capacitor/core";

let installed = false;

export async function initNativeKeyboard() {
    if (installed) return;
    installed = true;

    if (!Capacitor.isNativePlatform()) {
        // On web we rely purely on `visualViewport` for the same behavior.
        installVisualViewportBridge();
        return;
    }

    try {
        const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
        // Ensure the WebView resizes when the keyboard shows.
        await Keyboard.setResizeMode({ mode: KeyboardResize.Native });
        // Kill the accessory bar (^ v ✓)
        await Keyboard.setAccessoryBarVisible({ isVisible: false });

        // We DO NOT publish `--keyboard-h` on native — the WebView
        // itself has already shrunk under the keyboard via
        // `KeyboardResize.Native`, so the chat wrapper is at exactly
        // the right height without any extra CSS math.  We only add
        // the `.keyboard-open` marker class so components can hide
        // secondary UI (e.g. the pinned board) if they want to.
        Keyboard.addListener("keyboardWillShow", () => {
            document.documentElement.classList.add("keyboard-open");
        });
        Keyboard.addListener("keyboardWillHide", () => {
            document.documentElement.classList.remove("keyboard-open");
        });
    } catch (e) {
        // Plugin might not be linked in older bundles — fall back to VV.
        installVisualViewportBridge();
    }
}

// Web / PWA fallback — track visualViewport height so the composer can
// stick to the top of the mobile browser's software keyboard on iOS Safari
// and Chrome Android.  We publish the delta on --keyboard-h.
function installVisualViewportBridge() {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => {
        const delta = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
        document.documentElement.style.setProperty("--keyboard-h", `${delta}px`);
        document.documentElement.classList.toggle("keyboard-open", delta > 40);
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update();
}
